import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Play, Gauge, Volume2, VolumeX, CheckCircle, Monitor, Smartphone, Search, Headphones } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';

const GAME_STATES = {
  READY: 'ready',
  DEVICE_SELECT: 'device_select',
  STAGE_INTRO: 'stage_intro',
  PLAYING: 'playing',
  STAGE_RESULT: 'stage_result',
  FINISHED: 'finished',
};

const STAGES = {
  LIFT_COUNTING: 1,      // Audio only: count low tones
  TELEPHONE_SEARCH: 2,   // Visual only: find symbols
  COMBINED: 3,           // Both simultaneously
};

// Device Mode Selection Component
const DeviceModeSelector = ({ onSelect }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-8">
      <div className="text-center mb-6">
        <h2 className="font-heading text-xl font-bold text-[#0B1D51] mb-2">
          Select Your Device
        </h2>
        <p className="text-sm text-[#64748B]">
          Choose how you're taking this test for the best experience
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#0B1D51] hover:bg-[#0B1D51]/5"
          onClick={() => onSelect('desktop')}
          data-testid="select-desktop"
        >
          <Monitor className="w-10 h-10 text-[#0B1D51]" />
          <div>
            <p className="font-semibold">Laptop/Desktop</p>
            <p className="text-xs text-[#64748B]">Keyboard controls</p>
          </div>
        </Button>
        
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#FF9F1C] hover:bg-[#FF9F1C]/5"
          onClick={() => onSelect('mobile')}
          data-testid="select-mobile"
        >
          <Smartphone className="w-10 h-10 text-[#FF9F1C]" />
          <div>
            <p className="font-semibold">Mobile/Tablet</p>
            <p className="text-xs text-[#64748B]">Touch controls</p>
          </div>
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Business types with icons
const BUSINESS_TYPES = [
  { type: 'Plumber', icon: '🔧' },
  { type: 'Electrician', icon: '⚡' },
  { type: 'Solicitor', icon: '⚖️' },
  { type: 'Accountant', icon: '📊' },
  { type: 'Doctor', icon: '🏥' },
  { type: 'Dentist', icon: '🦷' },
  { type: 'Builder', icon: '🏗️' },
  { type: 'Garage', icon: '🚗' },
  { type: 'Restaurant', icon: '🍽️' },
  { type: 'Hotel', icon: '🏨' },
];

// Towns/Cities
const TOWNS = [
  'London', 'Manchester', 'Birmingham', 'Leeds', 'Glasgow', 
  'Liverpool', 'Bristol', 'Sheffield', 'Edinburgh', 'Cardiff',
  'Newcastle', 'Nottingham', 'Southampton', 'Leicester', 'Brighton'
];

// Country codes
const COUNTRY_CODES = ['+44', '+353', '+1', '+49', '+33'];

// Local area codes (UK style)
const LOCAL_CODES = ['0121', '0161', '0131', '0141', '0113', '0117', '0151', '0191', '0114', '0115'];

// Surnames for directory
const SURNAMES = [
  'Smith', 'Jones', 'Brown', 'Wilson', 'Taylor', 
  'Davies', 'Evans', 'Thomas', 'Roberts', 'Johnson',
  'Williams', 'Wright', 'Thompson', 'Hughes', 'Edwards'
];

// Target types for visual search - varies what users need to find
const TARGET_TYPES = {
  SYMBOL: 'symbol',
  INITIAL: 'initial', 
  COUNTRY_CODE: 'country_code',
  LOCAL_CODE: 'local_code',
  TOWN: 'town',
};

// Get level-based target configuration
const getLevelTargetConfig = (level) => {
  // Level 1-2: Single target (symbol or initial)
  // Level 3: Single target (any type)
  // Level 4-5: Multiple targets to count
  
  const allTargetOptions = [
    { type: TARGET_TYPES.SYMBOL, value: '@', label: 'the @ symbol', column: 'symbol' },
    { type: TARGET_TYPES.SYMBOL, value: '#', label: 'the # symbol', column: 'symbol' },
    { type: TARGET_TYPES.SYMBOL, value: '&', label: 'the & symbol', column: 'symbol' },
    { type: TARGET_TYPES.INITIAL, value: 'S', label: 'names starting with S', column: 'initial' },
    { type: TARGET_TYPES.INITIAL, value: 'J', label: 'names starting with J', column: 'initial' },
    { type: TARGET_TYPES.INITIAL, value: 'M', label: 'names starting with M', column: 'initial' },
    { type: TARGET_TYPES.COUNTRY_CODE, value: '+44', label: 'UK numbers (+44)', column: 'countryCode' },
    { type: TARGET_TYPES.COUNTRY_CODE, value: '+353', label: 'Irish numbers (+353)', column: 'countryCode' },
    { type: TARGET_TYPES.LOCAL_CODE, value: '0121', label: 'Birmingham code (0121)', column: 'localCode' },
    { type: TARGET_TYPES.LOCAL_CODE, value: '0161', label: 'Manchester code (0161)', column: 'localCode' },
    { type: TARGET_TYPES.TOWN, value: 'London', label: 'London entries', column: 'town' },
    { type: TARGET_TYPES.TOWN, value: 'Manchester', label: 'Manchester entries', column: 'town' },
  ];
  
  if (level <= 2) {
    // Easy: Just symbols or initials
    const easyTargets = allTargetOptions.filter(t => 
      t.type === TARGET_TYPES.SYMBOL || t.type === TARGET_TYPES.INITIAL
    );
    const selected = easyTargets[Math.floor(Math.random() * easyTargets.length)];
    return [selected];
  } else if (level === 3) {
    // Medium: Any single target type
    const selected = allTargetOptions[Math.floor(Math.random() * allTargetOptions.length)];
    return [selected];
  } else {
    // Hard (Level 4-5): Two different targets to count simultaneously
    const shuffled = [...allTargetOptions].sort(() => Math.random() - 0.5);
    // Ensure we pick two different types
    const first = shuffled[0];
    const second = shuffled.find(t => t.type !== first.type && t.column !== first.column);
    return [first, second || shuffled[1]];
  }
};

// Generate telephone directory data with multiple columns
const generateDirectory = (targetConfigs, gridSize) => {
  // Create symbol list excluding any target symbols
  const allSymbols = ['@', '#', '$', '%', '&', '*', '+', '=', '~', '^'];
  const targetSymbols = targetConfigs
    .filter(c => c.type === TARGET_TYPES.SYMBOL)
    .map(c => c.value);
  const nonTargetSymbols = allSymbols.filter(s => !targetSymbols.includes(s));
  
  // Get target initials to exclude from random generation
  const targetInitials = targetConfigs
    .filter(c => c.type === TARGET_TYPES.INITIAL)
    .map(c => c.value);
  
  // Get target values for other types
  const targetCountryCodes = targetConfigs
    .filter(c => c.type === TARGET_TYPES.COUNTRY_CODE)
    .map(c => c.value);
  const targetLocalCodes = targetConfigs
    .filter(c => c.type === TARGET_TYPES.LOCAL_CODE)
    .map(c => c.value);
  const targetTowns = targetConfigs
    .filter(c => c.type === TARGET_TYPES.TOWN)
    .map(c => c.value);
  
  // Create filtered lists
  const nonTargetCountryCodes = COUNTRY_CODES.filter(c => !targetCountryCodes.includes(c));
  const nonTargetLocalCodes = LOCAL_CODES.filter(c => !targetLocalCodes.includes(c));
  const nonTargetTowns = TOWNS.filter(t => !targetTowns.includes(t));
  
  const directory = [];
  
  // For each target config, determine how many entries should match
  const targetCounts = targetConfigs.map(() => 3 + Math.floor(Math.random() * 3)); // 3-5 each
  
  for (let i = 0; i < gridSize; i++) {
    // Generate initial that is NOT a target initial
    let initial;
    do {
      initial = String.fromCharCode(65 + Math.floor(Math.random() * 26));
    } while (targetInitials.includes(initial));
    
    const surname = SURNAMES[Math.floor(Math.random() * SURNAMES.length)];
    
    // Use non-target values for random generation
    const town = nonTargetTowns.length > 0 
      ? nonTargetTowns[Math.floor(Math.random() * nonTargetTowns.length)]
      : TOWNS[Math.floor(Math.random() * TOWNS.length)];
    const business = BUSINESS_TYPES[Math.floor(Math.random() * BUSINESS_TYPES.length)];
    const countryCode = nonTargetCountryCodes.length > 0
      ? nonTargetCountryCodes[Math.floor(Math.random() * nonTargetCountryCodes.length)]
      : COUNTRY_CODES[Math.floor(Math.random() * COUNTRY_CODES.length)];
    const localCode = nonTargetLocalCodes.length > 0
      ? nonTargetLocalCodes[Math.floor(Math.random() * nonTargetLocalCodes.length)]
      : LOCAL_CODES[Math.floor(Math.random() * LOCAL_CODES.length)];
    const localNumber = `${Math.floor(Math.random() * 900) + 100} ${Math.floor(Math.random() * 9000) + 1000}`;
    
    // Use non-target symbols for random generation
    const symbol = nonTargetSymbols.length > 0
      ? nonTargetSymbols[Math.floor(Math.random() * nonTargetSymbols.length)]
      : allSymbols[Math.floor(Math.random() * allSymbols.length)];
    
    directory.push({
      id: i,
      name: `${initial}. ${surname}`,
      initial,
      town,
      businessType: business.type,
      businessIcon: business.icon,
      countryCode,
      localCode,
      localNumber,
      symbol,
      isTarget: {} // Will be filled per target type
    });
  }
  
  // Now assign targets for each config
  targetConfigs.forEach((config, configIndex) => {
    const numTargets = targetCounts[configIndex];
    let assigned = 0;
    
    // Shuffle indices and assign targets
    const shuffledIndices = [...Array(gridSize).keys()].sort(() => Math.random() - 0.5);
    
    for (const idx of shuffledIndices) {
      if (assigned >= numTargets) break;
      
      const entry = directory[idx];
      
      // Set the value to match the target
      if (config.type === TARGET_TYPES.SYMBOL) {
        entry.symbol = config.value;
        entry.isTarget[config.value] = true;
      } else if (config.type === TARGET_TYPES.INITIAL) {
        entry.name = `${config.value}. ${SURNAMES[Math.floor(Math.random() * SURNAMES.length)]}`;
        entry.initial = config.value;
        entry.isTarget[config.value] = true;
      } else if (config.type === TARGET_TYPES.COUNTRY_CODE) {
        entry.countryCode = config.value;
        entry.isTarget[config.value] = true;
      } else if (config.type === TARGET_TYPES.LOCAL_CODE) {
        entry.localCode = config.value;
        entry.isTarget[config.value] = true;
      } else if (config.type === TARGET_TYPES.TOWN) {
        entry.town = config.value;
        entry.isTarget[config.value] = true;
      }
      
      assigned++;
    }
  });
  
  return directory;
};

const DividedAttentionGame = ({ level, onComplete }) => {
  const [gameState, setGameState] = useState(GAME_STATES.READY);
  const [deviceMode, setDeviceMode] = useState(null);
  const [currentStage, setCurrentStage] = useState(STAGES.LIFT_COUNTING);
  const [score, setScore] = useState(0);
  
  // Audio task state
  const [lowToneCount, setLowToneCount] = useState(0);
  const [userToneCount, setUserToneCount] = useState(0);
  const [tonesPlaying, setTonesPlaying] = useState(false);
  const [tonesComplete, setTonesComplete] = useState(false);
  
  // Visual task state - now supports multiple target types
  const [directory, setDirectory] = useState([]);
  const [targetConfigs, setTargetConfigs] = useState([]);
  const [stageTargetConfigs, setStageTargetConfigs] = useState({}); // Store configs per stage
  const [foundTargets, setFoundTargets] = useState({}); // { targetValue: Set() }
  const [visualComplete, setVisualComplete] = useState(false);
  const [visualTimeLeft, setVisualTimeLeft] = useState(30);
  
  // Stage results
  const [stageResults, setStageResults] = useState({});
  
  const startTime = useRef(Date.now());
  const audioContext = useRef(null);
  const toneTimeout = useRef(null);
  const gridSize = deviceMode === 'mobile' ? 20 : 30;

  // Play a tone
  const playTone = useCallback((isLow) => {
    if (!audioContext.current) {
      audioContext.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioContext.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    // Low tone = 300Hz, High tone = 800Hz
    oscillator.frequency.value = isLow ? 300 : 800;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    
    oscillator.start();
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
    oscillator.stop(ctx.currentTime + 0.4);
  }, []);

  // Play sequence of tones
  const playToneSequence = useCallback(() => {
    setTonesPlaying(true);
    setTonesComplete(false);
    let count = 0;
    let played = 0;
    const numTones = 8 + Math.floor(Math.random() * 7) + level; // 8-15+ tones
    
    const playNext = () => {
      if (played >= numTones) {
        setLowToneCount(count);
        setTonesPlaying(false);
        setTonesComplete(true);
        return;
      }
      
      // ~40% chance of low tone
      const isLow = Math.random() < 0.4;
      if (isLow) count++;
      playTone(isLow);
      played++;
      
      // Random interval - slower at level 1, faster at level 5
      // Level 1: 900-1300ms, Level 5: 400-700ms
      const baseInterval = 900 - (level - 1) * 125; // 900, 775, 650, 525, 400
      const intervalVariance = 400 - (level - 1) * 50; // 400, 350, 300, 250, 200
      toneTimeout.current = setTimeout(playNext, baseInterval + Math.random() * intervalVariance);
    };
    
    // Start after brief delay
    setTimeout(playNext, 1000);
  }, [level, playTone]);

  // Visual timer for stages 2 and 3
  useEffect(() => {
    if (gameState !== GAME_STATES.PLAYING) return;
    if (currentStage === STAGES.LIFT_COUNTING) return; // No timer for audio-only
    
    const timer = setInterval(() => {
      setVisualTimeLeft(prev => {
        if (prev <= 0) {
          setVisualComplete(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [gameState, currentStage]);

  // Start tone sequence for stages 1 and 3
  useEffect(() => {
    if (gameState !== GAME_STATES.PLAYING) return;
    if (currentStage === STAGES.TELEPHONE_SEARCH) return; // No audio for visual-only
    
    playToneSequence();
    
    return () => {
      if (toneTimeout.current) clearTimeout(toneTimeout.current);
    };
  }, [gameState, currentStage, playToneSequence]);

  // Initialize directory for stages 2 and 3 - use the SAME targets shown in instructions
  useEffect(() => {
    if (gameState !== GAME_STATES.PLAYING) return;
    if (currentStage === STAGES.LIFT_COUNTING) return;
    
    // Use the stored target configs from when the stage intro was shown
    const storedConfigs = stageTargetConfigs[currentStage] || targetConfigs;
    
    if (storedConfigs && storedConfigs.length > 0) {
      setTargetConfigs(storedConfigs);
      setDirectory(generateDirectory(storedConfigs, gridSize));
      // Initialize found targets object with empty sets for each target value
      const initialFound = {};
      storedConfigs.forEach(config => {
        initialFound[config.value] = new Set();
      });
      setFoundTargets(initialFound);
    }
    
    setVisualTimeLeft(currentStage === STAGES.COMBINED ? 45 : 30);
    setVisualComplete(false);
  }, [gameState, currentStage, gridSize, stageTargetConfigs]);

  const handleDirectoryClick = (item) => {
    if (visualComplete) return;
    
    // Check each target config to see if this item matches
    targetConfigs.forEach(config => {
      if (item.isTarget[config.value] && !foundTargets[config.value]?.has(item.id)) {
        setFoundTargets(prev => ({
          ...prev,
          [config.value]: new Set([...(prev[config.value] || []), item.id])
        }));
      }
    });
  };

  const submitToneCount = () => {
    const correct = userToneCount === lowToneCount;
    const stageScore = correct ? 30 : Math.max(0, 30 - Math.abs(userToneCount - lowToneCount) * 5);
    
    setStageResults(prev => ({
      ...prev,
      [currentStage]: {
        ...prev[currentStage],
        toneScore: stageScore,
        actualCount: lowToneCount,
        userCount: userToneCount,
        toneCorrect: correct
      }
    }));
    
    setScore(s => s + stageScore);
    
    // If stage 1, show result. If stage 3, check if visual also done
    if (currentStage === STAGES.LIFT_COUNTING || visualComplete) {
      calculateStageResult();
    }
  };

  const submitVisualSearch = () => {
    // Calculate score across all target types
    let totalTargets = 0;
    let totalFound = 0;
    
    targetConfigs.forEach(config => {
      const targetsForThisType = directory.filter(d => d.isTarget[config.value]).length;
      const foundForThisType = foundTargets[config.value]?.size || 0;
      totalTargets += targetsForThisType;
      totalFound += foundForThisType;
    });
    
    const visualScore = totalTargets > 0 ? Math.round((totalFound / totalTargets) * 30) : 0;
    
    setStageResults(prev => ({
      ...prev,
      [currentStage]: {
        ...prev[currentStage],
        visualScore,
        foundCount: totalFound,
        totalTargets,
        visualComplete: true,
        targetConfigs: targetConfigs.map(c => c.label)
      }
    }));
    
    setScore(s => s + visualScore);
    setVisualComplete(true);
    
    // If stage 2, show result. If stage 3, check if tones also done
    if (currentStage === STAGES.TELEPHONE_SEARCH || tonesComplete) {
      calculateStageResult();
    }
  };

  const calculateStageResult = () => {
    setGameState(GAME_STATES.STAGE_RESULT);
  };

  const nextStage = () => {
    if (currentStage < STAGES.COMBINED) {
      setCurrentStage(s => s + 1);
      setTonesComplete(false);
      setVisualComplete(false);
      setUserToneCount(0);
      setLowToneCount(0);
      setGameState(GAME_STATES.STAGE_INTRO);
    } else {
      finishGame();
    }
  };

  const finishGame = () => {
    setGameState(GAME_STATES.FINISHED);
    const timeSpent = Math.round((Date.now() - startTime.current) / 1000);
    // Stage 1: 30 (tones), Stage 2: 30 (visual), Stage 3: 60 (tones + visual)
    const maxScore = 120;
    const accuracy = (score / maxScore) * 100;
    onComplete({ score, maxScore, accuracy, timeSpent });
  };

  const startStage = () => {
    setGameState(GAME_STATES.PLAYING);
  };

  const startGame = () => {
    setCurrentStage(STAGES.LIFT_COUNTING);
    setScore(0);
    setStageResults({});
    setStageTargetConfigs({}); // Reset stored configs
    startTime.current = Date.now();
    setGameState(GAME_STATES.STAGE_INTRO);
  };
  
  // Generate and store target configs when entering stage intro for visual stages
  useEffect(() => {
    if (gameState === GAME_STATES.STAGE_INTRO && (currentStage === STAGES.TELEPHONE_SEARCH || currentStage === STAGES.COMBINED)) {
      // Generate targets for this stage and store them
      const newConfigs = getLevelTargetConfig(level);
      setTargetConfigs(newConfigs);
      setStageTargetConfigs(prev => ({
        ...prev,
        [currentStage]: newConfigs
      }));
    }
  }, [gameState, currentStage, level]);

  // Ready screen - device selection
  if (gameState === GAME_STATES.READY) {
    return <DeviceModeSelector onSelect={(mode) => { setDeviceMode(mode); setGameState(GAME_STATES.DEVICE_SELECT); }} />;
  }

  // Instructions screen
  if (gameState === GAME_STATES.DEVICE_SELECT) {
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-6 sm:pt-8">
          <div className="text-center mb-4 sm:mb-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-[#7C3AED]/10 rounded-full flex items-center justify-center">
              <Gauge className="w-8 h-8 sm:w-10 sm:h-10 text-[#7C3AED]" />
            </div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#0B1D51] mb-2">
              Dual-Task Attention
            </h2>
            <p className="text-sm text-[#64748B]">Level {level} - Three-Stage Assessment</p>
            <p className="text-xs text-[#FF9F1C] font-medium mt-2">
              Develops skills to improve performance on the TEA-Occ Divided Attention Test
            </p>
          </div>
          
          <div className="bg-[#F8FAFC] rounded-sm p-3 sm:p-4 mb-4 sm:mb-6">
            <h3 className="font-semibold text-[#0B1D51] mb-3 text-sm sm:text-base">This test has 3 stages:</h3>
            <ol className="space-y-3 text-xs sm:text-sm text-[#64748B]">
              <li className="flex gap-3 items-start">
                <Badge className="bg-[#0B1D51] shrink-0">1</Badge>
                <div>
                  <strong>Lift Counting (Audio Only)</strong>
                  <p>Listen to tones. Count only the LOW tones, ignore HIGH tones.</p>
                </div>
              </li>
              <li className="flex gap-3 items-start">
                <Badge className="bg-[#7C3AED] shrink-0">2</Badge>
                <div>
                  <strong>Telephone Search (Visual Only)</strong>
                  <p>Find all entries matching specific criteria in the directory (varies by level).</p>
                </div>
              </li>
              <li className="flex gap-3 items-start">
                <Badge className="bg-[#FF9F1C] shrink-0">3</Badge>
                <div>
                  <strong>Combined Task</strong>
                  <p>Do BOTH tasks simultaneously - count tones while searching!</p>
                </div>
              </li>
            </ol>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4 sm:mb-6">
            <div className="bg-slate-100 rounded-sm p-3 text-center">
              <Volume2 className="w-6 h-6 mx-auto mb-2 text-[#2CB67D]" />
              <p className="text-xs text-[#64748B]">LOW tone</p>
              <p className="font-mono text-sm font-bold text-[#2CB67D]">Count these</p>
            </div>
            <div className="bg-slate-100 rounded-sm p-3 text-center">
              <VolumeX className="w-6 h-6 mx-auto mb-2 text-[#EF4444]" />
              <p className="text-xs text-[#64748B]">HIGH tone</p>
              <p className="font-mono text-sm font-bold text-[#EF4444]">Ignore these</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4 sm:mb-6">
            <p className="text-xs sm:text-sm text-amber-800">
              <strong>RIS-3751-TOM:</strong> This exercise develops skills assessed by the TEA-Occ (Test of Everyday Attention for Occupational Context) - measuring ability to divide attention between auditory and visual tasks simultaneously.
            </p>
          </div>
          
          <Button 
            onClick={startGame}
            size="lg"
            className="w-full bg-[#0B1D51] hover:bg-[#0B1D51]/90"
            data-testid="start-game-btn"
          >
            <Play className="w-5 h-5 mr-2" />
            Begin Assessment
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Stage intro screen
  if (gameState === GAME_STATES.STAGE_INTRO) {
    const stageInfo = {
      [STAGES.LIFT_COUNTING]: {
        title: "Stage 1: Lift Counting",
        icon: Headphones,
        color: "#0B1D51",
        description: "Listen to the sequence of tones. Count only the LOW-pitched tones and ignore the HIGH-pitched ones.",
        instructions: [
          "Tones will play automatically",
          "LOW tones are deeper (300Hz) - COUNT these",
          "HIGH tones are higher (800Hz) - IGNORE these",
          "Enter your count when the sequence ends"
        ]
      },
      [STAGES.TELEPHONE_SEARCH]: {
        title: "Stage 2: Telephone Search",
        icon: Search,
        color: "#7C3AED",
        description: `Search the telephone directory and find all entries matching: ${targetConfigs.map(c => c.label).join(' AND ')}.`,
        instructions: [
          `Find: ${targetConfigs.map(c => c.label).join(', ')}`,
          "You have 30 seconds to find all targets",
          "Work quickly but accurately",
          level >= 4 ? "This level has MULTIPLE criteria to find!" : "Click/tap matching entries"
        ]
      },
      [STAGES.COMBINED]: {
        title: "Stage 3: Combined Task",
        icon: Gauge,
        color: "#FF9F1C",
        description: "Now do BOTH tasks at once! Count low tones while searching the directory.",
        instructions: [
          "Tones will play while you search",
          `Find: ${targetConfigs.map(c => c.label).join(', ')}`,
          "Keep count of LOW tones in your head",
          "You'll submit both answers at the end"
        ]
      }
    };

    const info = stageInfo[currentStage];
    const Icon = info.icon;

    return (
      <Card className="border-slate-200">
        <CardContent className="pt-6 sm:pt-8">
          <div className="text-center mb-4 sm:mb-6">
            <div 
              className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${info.color}20` }}
            >
              <Icon className="w-8 h-8 sm:w-10 sm:h-10" style={{ color: info.color }} />
            </div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#0B1D51] mb-2">
              {info.title}
            </h2>
            <p className="text-sm text-[#64748B]">{info.description}</p>
          </div>
          
          <div className="bg-[#F8FAFC] rounded-sm p-3 sm:p-4 mb-4 sm:mb-6">
            <h3 className="font-semibold text-[#0B1D51] mb-3 text-sm sm:text-base">Instructions:</h3>
            <ul className="space-y-2 text-xs sm:text-sm text-[#64748B]">
              {info.instructions.map((inst, idx) => (
                <li key={idx} className="flex gap-2">
                  <span className="font-mono text-[#0B1D51]">{idx + 1}.</span>
                  {inst}
                </li>
              ))}
            </ul>
          </div>
          
          <Button 
            onClick={startStage}
            size="lg"
            className="w-full"
            style={{ backgroundColor: info.color }}
            data-testid="start-stage-btn"
          >
            <Play className="w-5 h-5 mr-2" />
            Start Stage {currentStage}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Playing - Stage 1: Lift Counting (Audio Only)
  if (gameState === GAME_STATES.PLAYING && currentStage === STAGES.LIFT_COUNTING) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-center justify-between">
          <Badge className="bg-[#0B1D51]">Stage 1: Lift Counting</Badge>
          <p className="text-sm text-[#64748B]">Score: {score}</p>
        </div>

        <Card className="border-slate-200">
          <CardContent className="py-8 text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-[#0B1D51]/10 rounded-full flex items-center justify-center">
              <Headphones className={`w-12 h-12 text-[#0B1D51] ${tonesPlaying ? 'animate-pulse' : ''}`} />
            </div>
            
            {tonesPlaying && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mb-6"
              >
                <p className="text-lg font-semibold text-[#0B1D51] mb-2">Tones Playing...</p>
                <p className="text-sm text-[#64748B]">Count the LOW tones (ignore HIGH)</p>
                <div className="mt-4 flex justify-center gap-4">
                  <div className="text-center">
                    <div className="w-3 h-3 bg-[#2CB67D] rounded-full animate-ping mx-auto" />
                    <p className="text-xs text-[#64748B] mt-1">LOW = Count</p>
                  </div>
                  <div className="text-center">
                    <div className="w-3 h-3 bg-[#EF4444] rounded-full mx-auto" />
                    <p className="text-xs text-[#64748B] mt-1">HIGH = Ignore</p>
                  </div>
                </div>
              </motion.div>
            )}
            
            {tonesComplete && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <p className="text-lg font-semibold text-[#0B1D51] mb-4">Sequence Complete!</p>
                <p className="text-sm text-[#64748B] mb-4">How many LOW tones did you count?</p>
                
                <div className="flex items-center justify-center gap-4 mb-6">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setUserToneCount(c => Math.max(0, c - 1))}
                  >
                    -
                  </Button>
                  <span className="font-mono text-4xl font-bold text-[#0B1D51] w-16 text-center">
                    {userToneCount}
                  </span>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setUserToneCount(c => c + 1)}
                  >
                    +
                  </Button>
                </div>
                
                <Button 
                  onClick={submitToneCount}
                  className="w-full bg-[#2CB67D] hover:bg-[#2CB67D]/90"
                  data-testid="submit-count-btn"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Submit Count
                </Button>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Playing - Stage 2: Telephone Search (Visual Only)
  if (gameState === GAME_STATES.PLAYING && currentStage === STAGES.TELEPHONE_SEARCH) {
    // Calculate total targets across all configs
    let totalTargets = 0;
    let totalFound = 0;
    targetConfigs.forEach(config => {
      totalTargets += directory.filter(d => d.isTarget[config.value]).length;
      totalFound += foundTargets[config.value]?.size || 0;
    });
    
    // Check if an item is found for any target
    const isItemFound = (item) => {
      return targetConfigs.some(config => 
        item.isTarget[config.value] && foundTargets[config.value]?.has(item.id)
      );
    };
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Badge className="bg-[#7C3AED]">Stage 2: Telephone Search</Badge>
          <div className="text-right">
            <p className="text-xs text-[#64748B]">Time Left</p>
            <p className="font-mono font-bold text-[#0B1D51]">{visualTimeLeft}s</p>
          </div>
        </div>
        
        <div className="bg-white border rounded-sm p-2">
          <p className="text-sm mb-1">
            Find all entries with: 
            {targetConfigs.map((config, idx) => (
              <span key={config.value}>
                {idx > 0 && <span className="text-[#64748B]"> AND </span>}
                <strong className="text-[#7C3AED]">{config.label}</strong>
              </span>
            ))}
          </p>
          <p className="text-sm text-[#64748B]">Found: {totalFound}/{totalTargets}</p>
        </div>
        
        <Progress value={totalTargets > 0 ? (totalFound / totalTargets) * 100 : 0} className="h-2" />

        <Card className="border-slate-200 max-h-[400px] overflow-y-auto">
          <CardContent className="py-2">
            {/* Column headers */}
            <div className="grid grid-cols-12 gap-1 text-xs font-medium text-[#64748B] border-b pb-1 mb-1 sticky top-0 bg-white">
              <span className="col-span-2">Name</span>
              <span className="col-span-2">Town</span>
              <span className="col-span-1 text-center">Type</span>
              <span className="col-span-1 text-center">CC</span>
              <span className="col-span-2">Local</span>
              <span className="col-span-3">Number</span>
              <span className="col-span-1 text-center">Sym</span>
            </div>
            <div className="space-y-1">
              {directory.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleDirectoryClick(item)}
                  disabled={visualComplete || isItemFound(item)}
                  className={`w-full text-left p-1.5 rounded text-xs grid grid-cols-12 gap-1 items-center transition-colors ${
                    isItemFound(item)
                      ? 'bg-[#2CB67D]/20 border border-[#2CB67D]'
                      : 'hover:bg-slate-50 border border-transparent'
                  }`}
                  data-testid={`directory-${item.id}`}
                >
                  <span className="col-span-2 font-medium truncate">{item.name}</span>
                  <span className="col-span-2 text-[#64748B] truncate">{item.town}</span>
                  <span className="col-span-1 text-center" title={item.businessType}>{item.businessIcon}</span>
                  <span className="col-span-1 text-center font-mono text-[#64748B]">{item.countryCode}</span>
                  <span className="col-span-2 font-mono text-[#64748B]">{item.localCode}</span>
                  <span className="col-span-3 font-mono text-[#64748B]">{item.localNumber}</span>
                  <span className="col-span-1 text-center text-lg">{item.symbol}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Button 
          onClick={submitVisualSearch}
          className="w-full bg-[#2CB67D] hover:bg-[#2CB67D]/90"
          data-testid="submit-search-btn"
        >
          <CheckCircle className="w-5 h-5 mr-2" />
          Submit Search Results
        </Button>
      </div>
    );
  }

  // Playing - Stage 3: Combined Task
  if (gameState === GAME_STATES.PLAYING && currentStage === STAGES.COMBINED) {
    // Calculate total targets across all configs
    let totalTargets = 0;
    let totalFound = 0;
    targetConfigs.forEach(config => {
      totalTargets += directory.filter(d => d.isTarget[config.value]).length;
      totalFound += foundTargets[config.value]?.size || 0;
    });
    
    const showToneInput = tonesComplete && !stageResults[STAGES.COMBINED]?.toneScore;
    
    // Check if an item is found for any target
    const isItemFound = (item) => {
      return targetConfigs.some(config => 
        item.isTarget[config.value] && foundTargets[config.value]?.has(item.id)
      );
    };
    
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Badge className="bg-[#FF9F1C]">Stage 3: Combined</Badge>
          <div className="flex gap-4">
            {tonesPlaying && (
              <div className="flex items-center gap-1">
                <Headphones className="w-4 h-4 text-[#2CB67D] animate-pulse" />
                <span className="text-xs text-[#64748B]">Counting...</span>
              </div>
            )}
            <div className="text-right">
              <p className="text-xs text-[#64748B]">Time</p>
              <p className="font-mono font-bold text-[#0B1D51]">{visualTimeLeft}s</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white border rounded-sm p-2">
          <p className="text-sm">
            Find: 
            {targetConfigs.map((config, idx) => (
              <span key={config.value}>
                {idx > 0 && <span className="text-[#64748B]"> AND </span>}
                <strong className="text-[#FF9F1C]">{config.label}</strong>
              </span>
            ))}
          </p>
          <p className="text-xs text-[#64748B]">Found: {totalFound}/{totalTargets}</p>
        </div>

        <Card className="border-slate-200 max-h-[300px] overflow-y-auto">
          <CardContent className="py-2">
            {/* Column headers */}
            <div className="grid grid-cols-12 gap-1 text-xs font-medium text-[#64748B] border-b pb-1 mb-1 sticky top-0 bg-white">
              <span className="col-span-2">Name</span>
              <span className="col-span-2">Town</span>
              <span className="col-span-1 text-center">Type</span>
              <span className="col-span-1 text-center">CC</span>
              <span className="col-span-2">Local</span>
              <span className="col-span-3">Number</span>
              <span className="col-span-1 text-center">Sym</span>
            </div>
            <div className="space-y-1">
              {directory.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleDirectoryClick(item)}
                  disabled={visualComplete || isItemFound(item)}
                  className={`w-full text-left p-1.5 rounded text-xs grid grid-cols-12 gap-1 items-center transition-colors ${
                    isItemFound(item)
                      ? 'bg-[#2CB67D]/20 border border-[#2CB67D]'
                      : 'hover:bg-slate-50 border border-transparent'
                  }`}
                >
                  <span className="col-span-2 font-medium truncate">{item.name}</span>
                  <span className="col-span-2 text-[#64748B] truncate">{item.town}</span>
                  <span className="col-span-1 text-center" title={item.businessType}>{item.businessIcon}</span>
                  <span className="col-span-1 text-center font-mono text-[#64748B]">{item.countryCode}</span>
                  <span className="col-span-2 font-mono text-[#64748B]">{item.localCode}</span>
                  <span className="col-span-3 font-mono text-[#64748B]">{item.localNumber}</span>
                  <span className="col-span-1 text-center text-lg">{item.symbol}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {showToneInput && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="border-[#FF9F1C] border-2">
              <CardContent className="py-4">
                <p className="text-center text-sm font-medium text-[#0B1D51] mb-3">
                  Tones finished! How many LOW tones?
                </p>
                <div className="flex items-center justify-center gap-4 mb-3">
                  <Button
                    variant="outline"
                    onClick={() => setUserToneCount(c => Math.max(0, c - 1))}
                  >
                    -
                  </Button>
                  <span className="font-mono text-3xl font-bold text-[#0B1D51] w-12 text-center">
                    {userToneCount}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setUserToneCount(c => c + 1)}
                  >
                    +
                  </Button>
                </div>
                <Button 
                  onClick={submitToneCount}
                  className="w-full bg-[#0B1D51]"
                  size="sm"
                >
                  Submit Count
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {(!showToneInput || stageResults[STAGES.COMBINED]?.toneScore) && (
          <Button 
            onClick={submitVisualSearch}
            className="w-full bg-[#2CB67D] hover:bg-[#2CB67D]/90"
            disabled={!stageResults[STAGES.COMBINED]?.toneScore}
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            Submit Search & Complete Stage
          </Button>
        )}
      </div>
    );
  }

  // Stage Result screen
  if (gameState === GAME_STATES.STAGE_RESULT) {
    const result = stageResults[currentStage] || {};
    const isLastStage = currentStage === STAGES.COMBINED;
    
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-6 sm:pt-8 text-center">
          <h2 className="font-heading text-xl font-bold text-[#0B1D51] mb-4">
            Stage {currentStage} Complete!
          </h2>
          
          <div className="space-y-4 mb-6">
            {result.toneScore !== undefined && (
              <div className="bg-slate-50 rounded-sm p-4">
                <p className="text-sm text-[#64748B] mb-1">Tone Counting</p>
                <p className="font-mono text-lg">
                  Your count: <strong>{result.userCount}</strong> | Actual: <strong>{result.actualCount}</strong>
                </p>
                <p className={`text-sm ${result.toneCorrect ? 'text-[#2CB67D]' : 'text-[#FF9F1C]'}`}>
                  {result.toneCorrect ? 'Perfect!' : `Off by ${Math.abs(result.userCount - result.actualCount)}`}
                </p>
              </div>
            )}
            
            {result.visualScore !== undefined && (
              <div className="bg-slate-50 rounded-sm p-4">
                <p className="text-sm text-[#64748B] mb-1">Symbol Search</p>
                <p className="font-mono text-lg">
                  Found: <strong>{result.foundCount}/{result.totalTargets}</strong>
                </p>
              </div>
            )}
          </div>
          
          <p className="text-2xl font-bold text-[#0B1D51] mb-6">
            Total Score: {score}
          </p>
          
          <Button 
            onClick={nextStage}
            className={`w-full ${isLastStage ? 'bg-[#2CB67D]' : 'bg-[#0B1D51]'}`}
          >
            {isLastStage ? 'View Final Results' : `Continue to Stage ${currentStage + 1}`}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default DividedAttentionGame;
